import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@project-mgmt/ui";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (props: { error: Error; resetError: () => void; retryCount: number }) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  maxRetries?: number;
  resetOnPropsChange?: boolean;
}

/**
 * Enhanced error boundary for catching page-level errors with retry logic
 */
export class PageErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState((prevState) => ({
      error,
      errorInfo,
      retryCount: prevState.retryCount + 1,
    }));

    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("Error caught by boundary:", error);
      console.error("Error info:", errorInfo);
      console.error("Component stack:", errorInfo.componentStack);
    }

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset error state when props change (if enabled)
    if (this.props.resetOnPropsChange && prevProps.children !== this.props.children) {
      if (this.state.hasError) {
        this.resetError();
      }
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    });
  };

  retryWithDelay = (delay: number = 1000) => {
    this.retryTimeoutId = setTimeout(() => {
      this.resetError();
    }, delay);
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const maxRetries = this.props.maxRetries || 3;
      const hasExceededRetries = this.state.retryCount > maxRetries;

      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          resetError: this.resetError,
          retryCount: this.state.retryCount,
        });
      }

      // Default error UI with enhanced recovery options
      return (
        <div className="bg-surface flex min-h-screen items-center justify-center">
          <div className="bg-surface shadow-card border-border w-full max-w-md rounded-lg border p-6 text-center">
            <div className="mb-4">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h2 className="text-textPrimary mb-2 text-xl font-semibold">Something went wrong</h2>
              <p className="text-textSecondary mb-4">{this.state.error.message || "An unexpected error occurred"}</p>

              {this.state.retryCount > 0 && (
                <p className="text-textSecondary mb-4 text-sm">
                  Retry attempt: {this.state.retryCount}/{maxRetries}
                </p>
              )}
            </div>

            <div className="space-y-3">
              {!hasExceededRetries && (
                <Button onClick={this.resetError} className="w-full">
                  Try Again
                </Button>
              )}

              <Button
                variant="outline"
                onClick={() => this.retryWithDelay(2000)}
                className="w-full"
                disabled={hasExceededRetries}
              >
                Retry in 2 seconds
              </Button>

              <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
                Reload Page
              </Button>

              {hasExceededRetries && (
                <Button
                  variant="outline"
                  onClick={() => {
                    localStorage.clear();
                    window.location.href = "/";
                  }}
                  className="w-full"
                >
                  Reset Application
                </Button>
              )}
            </div>

            {process.env.NODE_ENV === "development" && this.state.errorInfo && (
              <details className="mt-4 text-left">
                <summary className="text-textSecondary hover:text-textPrimary cursor-pointer text-sm">
                  Error Details (Development)
                </summary>
                <div className="mt-2 space-y-2">
                  <pre className="bg-surface border-border max-h-32 overflow-auto rounded border p-2 text-xs">
                    {this.state.error.stack}
                  </pre>
                  <pre className="bg-surface border-border max-h-32 overflow-auto rounded border p-2 text-xs">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Error boundary specifically for context-related errors with enhanced recovery
 */
export function ContextErrorBoundary({
  children,
  level = "workspace",
}: {
  children: ReactNode;
  level?: "workspace" | "project" | "task";
}) {
  const getRecoveryActions = (level: string) => {
    switch (level) {
      case "task":
        return {
          primary: "Go to Project",
          primaryAction: () => {
            const url = new URL(window.location.href);
            url.searchParams.delete("taskId");
            window.location.href = url.toString();
          },
          secondary: "Go to Workspace",
          secondaryAction: () => {
            const url = new URL(window.location.href);
            url.searchParams.delete("taskId");
            url.searchParams.delete("projectId");
            window.location.href = url.toString();
          },
        };
      case "project":
        return {
          primary: "Go to Workspace",
          primaryAction: () => {
            const url = new URL(window.location.href);
            url.searchParams.delete("projectId");
            url.searchParams.delete("taskId");
            window.location.href = url.toString();
          },
          secondary: "Select Workspace",
          secondaryAction: () => {
            localStorage.removeItem("app_last_workspace_id");
            window.location.href = "/workspaces";
          },
        };
      default:
        return {
          primary: "Select Workspace",
          primaryAction: () => {
            localStorage.removeItem("app_last_workspace_id");
            window.location.href = "/workspaces";
          },
          secondary: "Clear All Data",
          secondaryAction: () => {
            localStorage.clear();
            window.location.href = "/";
          },
        };
    }
  };

  return (
    <PageErrorBoundary
      maxRetries={2}
      resetOnPropsChange={true}
      fallback={({ resetError, retryCount }) => {
        const recovery = getRecoveryActions(level);

        return (
          <div className="bg-surface flex min-h-screen items-center justify-center">
            <div className="bg-surface shadow-card border-border w-full max-w-md rounded-lg border p-6 text-center">
              <div className="mb-4">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
                  <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-textPrimary mb-2 text-xl font-semibold">Context Error</h2>
                <p className="text-textSecondary mb-4">
                  There was an issue loading the {level} context. This might be due to network issues, invalid data, or
                  permission changes.
                </p>

                {retryCount > 0 && <p className="text-textSecondary mb-4 text-sm">Retry attempt: {retryCount}/2</p>}
              </div>

              <div className="space-y-3">
                {retryCount < 2 && (
                  <Button onClick={resetError} className="w-full">
                    Retry Loading
                  </Button>
                )}

                <Button variant="outline" onClick={recovery.primaryAction} className="w-full">
                  {recovery.primary}
                </Button>

                <Button variant="outline" onClick={recovery.secondaryAction} className="w-full">
                  {recovery.secondary}
                </Button>
              </div>
            </div>
          </div>
        );
      }}
      onError={(error, errorInfo) => {
        // Log context errors with additional context
        console.error(`Context error at ${level} level:`, error);
        console.error("Context error info:", errorInfo);

        // In production, you might want to send this to an error reporting service
        if (process.env.NODE_ENV === "production") {
          // Example: errorReportingService.captureException(error, { level, errorInfo });
        }
      }}
    >
      {children}
    </PageErrorBoundary>
  );
}

/**
 * Simple error fallback component for smaller errors
 */
export function SimpleErrorFallback({
  error,
  resetError,
  message = "Something went wrong",
  showDetails = false,
}: {
  error: Error;
  resetError: () => void;
  message?: string;
  showDetails?: boolean;
}) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
      <div className="mb-2 font-medium text-red-800">{message}</div>
      <div className="mb-3 text-sm text-red-600">{error.message}</div>
      <Button size="sm" onClick={resetError}>
        Try Again
      </Button>

      {showDetails && process.env.NODE_ENV === "development" && (
        <details className="mt-3 text-left">
          <summary className="cursor-pointer text-xs text-red-500 hover:text-red-700">Error Details</summary>
          <pre className="mt-1 max-h-20 overflow-auto rounded bg-red-100 p-2 text-xs">{error.stack}</pre>
        </details>
      )}
    </div>
  );
}

/**
 * Inline error fallback for form fields and small components
 */
export function InlineErrorFallback({
  error,
  resetError,
  compact = false,
}: {
  error: Error;
  resetError: () => void;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="flex items-center space-x-2 text-sm text-red-600">
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
        <span>Error loading</span>
        <button onClick={resetError} className="text-red-700 underline hover:text-red-900">
          retry
        </button>
      </div>
    );
  }

  return (
    <div className="border-l-4 border-red-400 bg-red-50 p-3">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm text-red-700">{error.message}</p>
          <button onClick={resetError} className="mt-1 text-sm text-red-700 underline hover:text-red-900">
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Network error fallback with specific recovery options
 */
export function NetworkErrorFallback({ resetError }: { error: Error; resetError: () => void }) {
  const isOffline = !navigator.onLine;

  return (
    <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
        <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>

      <h3 className="mb-2 font-medium text-orange-800">{isOffline ? "You're offline" : "Network Error"}</h3>

      <p className="mb-4 text-sm text-orange-600">
        {isOffline
          ? "Check your internet connection and try again."
          : "Unable to connect to the server. Please check your connection."}
      </p>

      <div className="space-y-2">
        <Button size="sm" onClick={resetError} className="w-full">
          {isOffline ? "Retry when online" : "Retry"}
        </Button>

        {!isOffline && (
          <button
            onClick={() => window.location.reload()}
            className="w-full text-sm text-orange-700 underline hover:text-orange-900"
          >
            Reload page
          </button>
        )}
      </div>
    </div>
  );
}
